"use client";

import { useEffect, useMemo, useRef } from "react";
import type { Socket } from "socket.io-client";
import { parseMessageCreatedEvent } from "~/lib/realtime/chatEvents";
import { acquireChatSocket, releaseChatSocket } from "~/lib/realtime/chatSocket";
import { retainConversation } from "~/lib/realtime/chatSubscriptions";
import { createSubscribeRetry, type SubscribeRetryController } from "~/lib/realtime/subscribeWithRetry";
import { api } from "~/trpc/react";

/**
 * A burst of several messages landing across different conversations (or a
 * reconnect resubscribing everything at once) should cost one inbox
 * refetch, not one per event.
 */
const REFRESH_DEBOUNCE_MS = 500;

/**
 * Keeps the inbox list live while it's open.
 *
 * Subscribes to every conversation currently LOADED in the list — bounded by
 * whatever the reader has actually scrolled to (`CONVERSATION_PAGE_SIZE` at a
 * time), never the account's full conversation history — and refetches the
 * list on any `message.created` for one of them, so a new message's preview,
 * ordering, and unread count show up without a manual refresh.
 *
 * Ordering, previews, and unread counts are all server-computed, so this
 * deliberately doesn't try to patch the cache itself the way
 * `useConversationSend.seed` does for a thread's messages — an invalidate is
 * one cheap request and can't drift from what the API would have said
 * anyway.
 *
 * A conversation loaded here AND open as a thread shares one subscription —
 * see `retainConversation` — so closing the thread never silently drops the
 * inbox's live updates for it.
 *
 * `enabled` gates the socket connection itself, not just the room
 * subscriptions — a signed-out caller (or one still resolving auth) must
 * not pay for a handshake it has no token for.
 *
 * `conversationIdsKey` is a SORTED, comma-joined id list rather than an
 * array: this hook's entire membership contract is "re-run when the SET of
 * loaded conversations changes, not when they reorder", and a primitive key
 * states that at the call site instead of relying on every caller happening
 * to pass a referentially-stable array. It also means a caller that already
 * holds the ids as a joined string (see `useMessagingUnreadCount`) doesn't
 * split it just for this hook to sort and re-join it.
 */
export function useInboxSocket(conversationIdsKey: string, enabled = true) {
  const utils = api.useUtils();
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const retainedRef = useRef<Map<string, () => void>>(new Map());
  // Owned by the socket effect below, read by the membership effect — the two
  // are deliberately separate (see the socket effect's comment), so the
  // controller has to be reachable across both.
  const retryRef = useRef<SubscribeRetryController | null>(null);
  const conversationIds = useMemo(() => conversationIdsKey ? conversationIdsKey.split(",") : [], [conversationIdsKey]);
  const idsRef = useRef<string[]>(conversationIds);
  idsRef.current = conversationIds;

  // Socket + listener lifecycle, kept independent of `conversationIdsKey`:
  // acquired once per mount and torn down once per unmount. If this were
  // combined with the membership effect, every reorder-driven re-run would
  // release every
  // retained room, drop the shared socket's refcount to 0, and force a fresh
  // reconnect (fresh handshake, fresh getSession()) followed by a resubscribe
  // burst for every already-loaded room — on every inbound message.
  useEffect(() => {
    if (!enabled) return;
    const socket = acquireChatSocket();
    socketRef.current = socket;
    if (!socket) return;
    const retry = createSubscribeRetry(socket);
    retryRef.current = retry;
    const subscribeAll = () => {
      for (const id of idsRef.current) retry.subscribe(id);
    };
    const scheduleRefresh = () => {
      if (refreshTimerRef.current) return;
      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null;
        void utils.messaging.listConversations.invalidate();
      }, REFRESH_DEBOUNCE_MS);
    };
    const handleMessageCreated = (raw: unknown) => {
      const event = parseMessageCreatedEvent(raw);
      if (!event || !idsRef.current.includes(event.conversation_id)) return;
      scheduleRefresh();
    };

    // `connect` fires on the first connection AND every reconnect — the
    // gateway holds no room membership across a dropped connection. The
    // already-connected case is handled by the membership effect below
    // (it subscribes every newly-retained id once the socket is connected),
    // not here — calling subscribeAll() on both would double-emit subscribe
    // for every id. The redundant emits would be harmless now that the
    // controller supersedes rather than stomps, but they are still wasted
    // round-trips against a gateway that rate-limits them.
    socket.on("connect", subscribeAll);
    socket.on("message.created", handleMessageCreated);
    return () => {
      socket.off("connect", subscribeAll);
      socket.off("message.created", handleMessageCreated);
      for (const release of retainedRef.current.values()) release();
      retainedRef.current.clear();
      retry.cancelAll();
      retryRef.current = null;
      socketRef.current = null;
      releaseChatSocket();
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [utils, enabled]);

  // Diffs against the previously retained set instead of releasing
  // everything on every membership change, so a conversation that's still
  // loaded is never released and re-subscribed just because another one
  // joined or left the list.
  //
  // Re-runs on `enabled` (not just `idsKey`) so `socketRef.current` gets
  // re-checked the moment the effect above acquires a socket. Every current
  // caller changes `idsKey` in the same tick `enabled` flips (the ids come
  // from a query gated by the same flag), so this has never been observed to
  // matter — but without it, a caller that already has a stable, non-empty
  // id list before `enabled` turns true would never retain those ids once
  // the socket becomes available, since `idsKey` alone wouldn't change to
  // re-trigger this effect.
  useEffect(() => {
    if (!enabled) return;
    const socket_0 = socketRef.current;
    if (!socket_0) return;
    const ids = conversationIds;
    const nextIds = new Set(ids);
    const retained = retainedRef.current;
    for (const [id_0, release_0] of retained) {
      if (!nextIds.has(id_0)) {
        release_0();
        retained.delete(id_0);
        retryRef.current?.cancel(id_0);
      }
    }
    for (const id_1 of ids) {
      if (retained.has(id_1)) continue;
      retained.set(id_1, retainConversation(socket_0, id_1));
      if (socket_0.connected) retryRef.current?.subscribe(id_1);
    }
  }, [conversationIds, enabled]);
}